import React, { useEffect, useState } from 'react';
import { Button, DropDownButton } from '@innovyze/stylovyze';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import DoneSharpIcon from '@mui/icons-material/DoneSharp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useGlobalization } from '@Translations/useGlobalization';
import StarIcon from '@mui/icons-material/Star';
import {
	StyledRiskMatrixDataGrid,
	useRiskMatrixStyles,
} from './RiskMatrixStyles';
import { Divider, Tooltip } from '@mui/material';
import { getSortedGridData } from './utils.RiskMatrix';
import Skeleton from '@mui/material/Skeleton';
import {
	getRiskMatrixPreview,
	setDidMatrixUpdate,
	setEditedCellsList,
	setOnlyGradesUpdated,
	setRiskSetupDirty,
	setUpdatedScores,
} from '@Actions/Risk.actions';
import { useDispatch } from 'react-redux';

import {
	generateRangeLabels,
	legends,
	legendsColors,
} from './utils.RiskMatrix';
import { RangeEditorDialog, RiskLegendMenu } from './RiskMatrixComponents';
import {
	PositionObj,
	RiskGradeRanges,
	RiskMatrixGridObj,
} from '@Types/risk.types';
import {
	selectUpdatedRiskRanges,
	selectRiskType,
	selectIsPreviewLoading,
	selectPreviewStatus,
	selectRiskSetting,
	selectIsLoading,
	selectRefreshMatrixGrid,
	selectUpdatedRiskMatrixGrid,
	selectEditedCellsList,
	selectIsPreviewOnLoad,
	selectEnablePreview,
	selectOnlyGradesUpdated,
	selectRiskRunning,
	selectPreviewCancelled,
} from '@Selectors';

interface RiskModeProps {
	setShowMethodSwitchDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export const RiskModeDropdown = ({
	setShowMethodSwitchDialog,
}: RiskModeProps) => {
	const riskType = selectRiskType();
	const { t } = useGlobalization();
	const classes = useRiskMatrixStyles();
	const isPreviewLoading = selectIsPreviewLoading();
	const isRiskRunning = selectRiskRunning();

	return (
		<DropDownButton
			width={140}
			switchSelected={false}
			disabled={isPreviewLoading || isRiskRunning}
			options={[
				{
					name: t('Risk Method'),
					cy: 'risk-method-dropdown-btn',
				},
				...[
					{ name: t('Linear'), riskType: 'linear' },
					{ name: `5x5 ${t('Matrix')}`, riskType: 'matrix' },
				].map(({ name: itemName, riskType: itemRiskType }, index) => ({
					name: itemName,
					key: index,
					cy: `risk-method-dropdown-item-${itemRiskType}`,
					icon:
						riskType === itemRiskType ? (
							<DoneSharpIcon className={classes.doneIcon} />
						) : (
							<span className={classes.doneIcon} />
						),
					disabled: riskType === itemRiskType,
					onClick: () => {
						setShowMethodSwitchDialog(true);
					},
				})),
			]}
		/>
	);
};

export const lofHeaderCell = () => {
	const { t } = useGlobalization();
	const classes = useRiskMatrixStyles();
	return (
		<div className={classes.lofcofheadercell} id="lofheadercell">
			<ArrowDownwardIcon className={classes.downIcon} />
			<div>{t('LoF / CoF')}</div>
			<ArrowForwardIcon className={classes.fwdIcon} />
		</div>
	);
};

export const cofHeaderCell = (rangeItem: string) => {
	const classes = useRiskMatrixStyles();
	return (
		<div className={classes.lofcofheadercell} id="cofheadercell">
			<div>&le; {rangeItem}</div>
		</div>
	);
};

export const lofCell = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	props: GridRenderCellParams<any, any>,
) => {
	const classes = useRiskMatrixStyles();
	const { value } = props;
	return (
		<div id="lof-cell" className={classes.lofCell}>
			<div> {value} </div>
		</div>
	);
};

export const RiskMatrixCell = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	props: GridRenderCellParams<any, RiskMatrixGridObj>,
) => {
	const { value } = props;
	const classes = useRiskMatrixStyles();
	const dispatch = useDispatch();
	const refreshMatrixGrid = selectRefreshMatrixGrid();
	const enablePreview = selectEnablePreview();
	const isLoading = selectIsLoading();
	const isPreviewLoading = selectIsPreviewLoading();
	const isDataLoading = isLoading || isPreviewLoading;
	const editedList = selectEditedCellsList();
	const isOnlyGradesUpdated = selectOnlyGradesUpdated();
	const riskRunning = selectRiskRunning();
	const cancellPreview = selectPreviewCancelled();

	const [grade, setGrade] = React.useState<string | undefined>(value?.grade);
	const [origGrade] = React.useState<string | undefined>(
		value?.grade ?? 'medium',
	);

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	function containsObject(obj: PositionObj) {
		for (let i = 0; i < editedList.length; i++) {
			if (editedList[i].x === obj.x && editedList[i].y === obj.y) {
				return true;
			}
		}
		return false;
	}

	useEffect(() => {
		if (
			value &&
			containsObject({ x: value.position.x, y: value.position.y })
		) {
			setGrade(origGrade);
		}
	}, [refreshMatrixGrid]);

	const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
		setAnchorEl(event.currentTarget);
	};

	let temp = selectUpdatedRiskMatrixGrid();
	const handleGradeChange = (event: React.MouseEvent<HTMLElement>) => {
		const newGradeValue = event.currentTarget.innerText.toLowerCase();
		if (newGradeValue !== grade) {
			setGrade(newGradeValue);
			if (value) {
				const newArray = temp.filter(function (el) {
					return (
						el.position.x !== value.position.x ||
						el.position.y !== value.position.y
					);
				});
				temp = [...newArray];
				temp.push({ ...value, grade: newGradeValue });
				dispatch(
					setEditedCellsList({
						x: value.position.x,
						y: value.position.y,
					}),
				);
			}
		}
		dispatch(setUpdatedScores(temp));
		dispatch(setOnlyGradesUpdated(true));
		dispatch(setDidMatrixUpdate(true));
		dispatch(setRiskSetupDirty(true));
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	if (isDataLoading) {
		return <Skeleton width={'90%'} height={'100%'} />;
	} else if (enablePreview && !isOnlyGradesUpdated) {
		return <></>;
	} else if (riskRunning) {
		return (
			<Tooltip
				title={`${value?.pipeCount} pipes`}
				placement="top"
				PopperProps={{
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [0, -5],
							},
						},
					],
				}}
				arrow>
				<div
					id="gridcell-container"
					className={classes.gridCellContainer}>
					<div
						id="gridcell-button"
						role="button"
						tabIndex={0}
						className={classes.cellButton}>
						<StarIcon
							style={{
								color: grade
									? legendsColors[
											grade as keyof typeof legendsColors
									  ]
									: 'rgb(239, 179, 4)',
								width: '20px',
								margin: '0px 5px -6px -3px',
							}}
						/>
						{cancellPreview ? '-' : value?.pipeCount}
					</div>
				</div>
			</Tooltip>
		);
	} else {
		return (
			<Tooltip
				title={`${value?.pipeCount} pipes`}
				placement="top"
				PopperProps={{
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [0, -5],
							},
						},
					],
				}}
				arrow>
				<div
					id="gridcell-container"
					className={classes.gridCellContainer}>
					<div
						id="gridcell-button"
						aria-controls={open ? 'risk-legend-menu' : undefined}
						aria-haspopup="true"
						aria-expanded={open ? 'true' : undefined}
						onClick={handleClick}
						onKeyDown={() => {
							return null;
						}}
						role="button"
						tabIndex={0}
						className={classes.cellButton}>
						<StarIcon
							style={{
								color: grade
									? legendsColors[
											grade as keyof typeof legendsColors
									  ]
									: 'rgb(239, 179, 4)',
								width: '20px',
								margin: '0px 5px -6px -3px',
							}}
						/>
						{cancellPreview ? '-' : value?.pipeCount}
					</div>
					<RiskLegendMenu
						anchorEl={anchorEl}
						open={open}
						selectedGrade={grade ?? 'medium'}
						handleClose={handleClose}
						handleGradeChange={handleGradeChange}
					/>
				</div>
			</Tooltip>
		);
	}
};

export const GetRiskMatrixColumns = () => {
	const headersList = new Array<GridColDef>();

	const ranges = selectUpdatedRiskRanges();
	const formattedCofRanges = generateRangeLabels(ranges.cofRanges);

	//add first column header - always remains same
	headersList.push({
		field: 'lofCof',
		renderHeader: lofHeaderCell,
		headerClassName: 'lofHeaderCellClass', //defined in StyledRiskMatrixDataGrid
		sortable: false,
		hideable: false,
		align: 'right',
		headerAlign: 'center',
		type: 'string',
		flex: 1,
		minWidth: 118,
		renderCell: lofCell,
		cellClassName: 'lofHeaderCellClass', //defined in StyledRiskMatrixDataGrid
	});

	//add rest of the column headers - comes from api data
	formattedCofRanges.forEach(rangeItem => {
		headersList.push({
			field: rangeItem,
			renderHeader: () => cofHeaderCell(rangeItem),
			sortable: false,
			hideable: false,
			align: 'left',
			headerAlign: 'left',
			flex: 1,
			minWidth: 95,
			renderCell: RiskMatrixCell,
			cellClassName: 'scoreCell', //defined in StyledRiskMatrixDataGrid
		});
	});

	return headersList;
};

const GetRiskMatrixRows = (matrixGrid: RiskMatrixGridObj[]) => {
	const gridCellsList = getSortedGridData(matrixGrid);
	const ranges = selectUpdatedRiskRanges();

	//get formatted values of API data -need to be flipped to correctly display data in Datagrid.
	const formattedLofRanges = generateRangeLabels(ranges.cofRanges);
	const formattedCofRanges = generateRangeLabels(ranges.lofRanges, true);

	const getCellValObjs = (rowIndex: number) => {
		const tempGridRow = gridCellsList[rowIndex];

		const mappedObj = formattedLofRanges.map(function (value, index) {
			return [formattedLofRanges[index], tempGridRow[index]];
		});

		const obj = Object.fromEntries(mappedObj);
		return obj;
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const gridRowsList: any[] = [];
	formattedCofRanges.forEach((item, key) => {
		const scores = getCellValObjs(key);
		const gridRow = { id: key, lofCof: item, ...scores };
		gridRowsList.push(gridRow);
	});
	return gridRowsList;
};

//**** MAIN COMPONENT - DATAGRID for Risk Matrix ****************/
export const RiskMatrixGrid = () => {
	const { t } = useGlobalization();
	const classes = useRiskMatrixStyles();

	const [showEditRangesDialog, setShowEditRangesDialog] =
		React.useState<boolean>(false);

	const dispatch = useDispatch();
	const riskSetting = selectRiskSetting();
	const configId = riskSetting.configId;
	const prevStatus = selectPreviewStatus();
	const matrixGrid = selectUpdatedRiskMatrixGrid();
	const isPreviewOnLoad = selectIsPreviewOnLoad();
	const cancellPreview = selectPreviewCancelled();

	//=========poll for preview=====//
	const [checkRiskPreviewStatusCounter, setCheckRiskPreviewStatusCounter] =
		useState<number>();
	const [ticker, setTicker] = useState<NodeJS.Timeout>();

	const refreshRiskPreviewStatus = (): void => {
		dispatch(
			getRiskMatrixPreview({
				configId: configId,
				initialLoad: isPreviewOnLoad,
			}),
		);
	};

	const isRiskConfigPreviewRunning = (value?: string): boolean => {
		return [
			'risk-calc-matrix-preview-in-progress',
			'risk-calc-matrix-preview-start',
			'risk-calc-matrix-preview-initiated',
		].includes(value ?? prevStatus);
	};

	useEffect(() => {
		if (checkRiskPreviewStatusCounter && isRiskConfigPreviewRunning()) {
			setTicker(
				setTimeout(() => {
					setCheckRiskPreviewStatusCounter(
						checkRiskPreviewStatusCounter + 1,
					);
				}, 5000),
			);
		}
	}, [checkRiskPreviewStatusCounter]);

	useEffect(() => {
		if (ticker) {
			refreshRiskPreviewStatus();
		}
	}, [ticker]);

	useEffect(() => {
		if (cancellPreview) {
			clearTimeout(ticker);
			setTicker(undefined);
		} else if (prevStatus) {
			if (ticker) {
				if (!isRiskConfigPreviewRunning()) {
					clearTimeout(ticker);
					setTicker(undefined);
				}
			} else if (isRiskConfigPreviewRunning()) {
				setCheckRiskPreviewStatusCounter(1);
			}
		}
	}, [prevStatus, cancellPreview]);
	//======poller==============

	const RiskMatrixHeader = () => {
		return (
			<>
				<div className={classes.matrixHeaderContainer}>
					<span className={classes.matrixHeaderText}>
						{'5x5 ' + t('Matrix')}
					</span>
				</div>
				<Divider />
			</>
		);
	};
	const RiskMatrixFooter = () => {
		const ranges: RiskGradeRanges = selectUpdatedRiskRanges();
		const cofRanges = [...ranges.cofRanges];
		const lofRanges = [...ranges.lofRanges];

		const isLoading = selectIsLoading();
		const isPreviewLoading = selectIsPreviewLoading();
		const isDataLoading = isLoading || isPreviewLoading;
		const riskRunning = selectRiskRunning();

		return (
			<>
				<div
					id="risk-grade-legends-list"
					className={classes.legendsList}>
					{legends.map((legend, key) => (
						<div
							key={key}
							className={classes.matrixFooterLegendDiv}>
							<StarIcon
								style={{
									width: '16px',
									marginBottom: '2px',
									color: legend.color,
								}}
							/>
							<span className={classes.legendSpans}>
								{t(legend.name.toUpperCase())}
							</span>
						</div>
					))}
				</div>
				<Divider />
				<div
					id="matrix-footer-container"
					className={classes.matrixFooterContainer}>
					<Button
						id="matrix-footer-btn-edit-ranges"
						variant="outlined"
						color="primary"
						className={classes.matrixFooterEditBtn}
						disabled={isDataLoading || riskRunning}
						onClick={() => setShowEditRangesDialog(true)}>
						{t('Edit Ranges')}
					</Button>
				</div>
				<div>
					<RangeEditorDialog
						showEditRangesDialog={showEditRangesDialog}
						setShowEditRangesDialog={setShowEditRangesDialog}
						cofRangeList={cofRanges}
						lofRangeList={lofRanges}
					/>
				</div>
			</>
		);
	};

	return (
		<StyledRiskMatrixDataGrid
			dataCy="risk-matrix-data-grid"
			disableColumnMenu
			disableColumnReorder
			skeletonRowCount={5}
			columns={GetRiskMatrixColumns()}
			rows={GetRiskMatrixRows(matrixGrid)}
			slots={{
				toolbar: RiskMatrixHeader,
				footer: RiskMatrixFooter,
			}}
		/>
	);
};
